import { ExperimentValue, type UseExperimentReturn, type UseExperimentReturnReadonly, type ExperimentOptions, type ExperimentNameUnion, type CoreExperiment } from '../types'
import { useCookie, useState, useDayjs, useCoreConfig, useCoreLogger, useUserInfo, readonly, computed } from '#imports'

/** `groupSize` consecutive user IDs share a bucket: 1 → A,B,A,B; 2 → A,A,B,B; … */
export function selectExperimentByUserId(userId: number | string, groupSize = 1): ExperimentValue {
    return Math.floor(Number(userId) / groupSize) % 2 === 0
        ? ExperimentValue.A
        : ExperimentValue.B
}

/**
 * Composable for reading or writing A/B experiment group assignments.
 *
 * `useExperiment` owns assignment; the experiments middleware only decides
 * when to call write mode. `assignmentMethod: 'userId'` derives the bucket
 * from the logged-in user's ID, which is authoritative over the cookie.
 * `needsAuthentication` assigns nothing while logged out unless a prior
 * cookie exists.
 *
 * **Read-only mode** (default): returns a readonly computed. Resolves the
 * userId bucket first (available before any cookie round-trip), then
 * `useState` on the server / the live cookie on the client, then `'a'`.
 * This is the mode components should use.
 *
 * **Write mode** (`allowWrite: true`): selects a group and persists it to
 * state and a cookie (30-day TTL). Only the experiment middleware should use
 * this mode. It must be the only `useCookie` ref writing the cookie — a
 * second ref for the same name created in the same tick cannot see the
 * first ref's write and would overwrite it.
 *
 * **Cookie config:** `sameSite: 'lax'`, `maxAge: 30 days`, `path: '/'`,
 * `secure: true`, `httpOnly: false`
 *
 * @param options - Experiment identification and write permission
 * @returns Ref containing the assigned group (`'a'`, `'b'`, etc.)
 *
 * @example
 * // Reading in a component (readonly)
 * const experiment = useExperiment({
 *     cookieName: ExperimentCookieName.LANDING_PAGE,
 *     experimentName: ExperimentName.LANDING_PAGE,
 * })
 * // experiment.value === 'a' | 'b'
 *
 * @example
 * // Writing in middleware
 * useExperiment({
 *     cookieName: experiment.cookieName,
 *     experimentName: experiment.name,
 *     allowWrite: true,
 * })
 */
export function useExperiment(options: ExperimentOptions & { allowWrite: true, experimentName: ExperimentNameUnion }): UseExperimentReturn
export function useExperiment(options: ExperimentOptions & { allowWrite?: false }): UseExperimentReturnReadonly
export function useExperiment(options: ExperimentOptions): unknown {
    const { cookieName, experimentName, allowWrite } = options
    const { env, experiments } = useCoreConfig()

    const logger = useCoreLogger().withTag('useExperiment')
    const state = useState<ExperimentValue>(cookieName)

    const experiment = (experiments as CoreExperiment[] | undefined)?.find(e => e.name === experimentName)
    const versions = experiment?.versions
    const isUserIdBased = experiment?.assignmentMethod === 'userId'

    const userInfo = isUserIdBased || experiment?.needsAuthentication ? useUserInfo() : undefined

    const userIdBucket = computed<ExperimentValue | undefined>(() => {
        if (!isUserIdBased) return undefined

        const userId = userInfo?.data.value?.id
        if (userId === undefined || userId === null) return undefined

        return selectExperimentByUserId(userId, experiment?.groupSize ?? 1)
    })

    const dayjs = useDayjs()
    const cookie = useCookie<ExperimentValue>(cookieName, {
        sameSite: 'lax',
        maxAge: dayjs.duration({ days: 30 }).asSeconds(), // 30 days
        path: '/',
        secure: true,
        httpOnly: false,
    })

    // Read-only consumers derive from state on SSR and from the cookie on the
    // client:
    //   - SSR: each `useCookie` call creates a fresh ref that reads request
    //     headers, so the middleware's write-mode cookie.value is invisible
    //     to a separate readonly call within the same request. `useState`
    //     IS shared by key across calls, so it reliably reflects the
    //     middleware's write within the render.
    //   - Client: the cookie is the live source of truth — it picks up
    //     middleware writes (via Set-Cookie → document.cookie), the
    //     cleanup middleware clearing retired cookies, and manual DevTools
    //     edits, all without re-entering write mode.
    // `state` is never written from the readonly path, so the original
    // poisoning bug (state stuck on 'a' from a pre-auth read) stays fixed.
    if (!allowWrite) {
        return readonly(computed(() => {
            if (userIdBucket.value) return userIdBucket.value
            if (import.meta.server) return state.value || ExperimentValue.A
            return cookie.value || ExperimentValue.A
        }))
    }

    if (state.value && (!userIdBucket.value || userIdBucket.value === state.value)) {
        if (userIdBucket.value && cookie.value !== userIdBucket.value) cookie.value = userIdBucket.value
        return state
    }

    function selectExperiment(experimentName: ExperimentNameUnion): ExperimentValue | undefined {
        if (!versions) {
            if (env === 'dev') {
                logger.error(`Missing experiment: "${experimentName}" from the "core" module in "nuxt.config.ts"`)
            }

            return ExperimentValue.A
        }

        const versionKeys = Object.keys(versions || {}) as ExperimentValue[]

        if (!versionKeys?.length) {
            if (env === 'dev') {
                logger.error(`Missing experiment: "${experimentName}" from the "core" module in "nuxt.config.ts"`)
            }

            return ExperimentValue.A
        }

        if (versionKeys.length === 1) {
            const firstVersionKey = versionKeys[0] as ExperimentValue

            if (env === 'dev') {
                logger.error(`Experiment has only set one option for the experiment: "${experimentName}" in the "core" module in "nuxt.config.ts". The experiment value will be set to: "${firstVersionKey}"`)
            }

            return firstVersionKey
        }

        if (userIdBucket.value) return userIdBucket.value

        const hasValidCookie = !!cookie.value && versionKeys.includes(cookie.value)

        if (isUserIdBased) return hasValidCookie ? cookie.value : undefined

        if (experiment?.needsAuthentication && !userInfo?.isLoggedIn.value) {
            return hasValidCookie ? cookie.value : undefined
        }

        if (hasValidCookie) return cookie.value

        const randomNumber = Math.floor(Math.random() * versionKeys.length)

        return versionKeys?.[randomNumber] as ExperimentValue
    }

    const selected = selectExperiment(experimentName)

    if (!selected) return state

    if (versions) {
        cookie.value = state.value = selected
    }
    else {
        state.value = selected
    }

    return state
}
